import { apiDelete, apiGet, apiPut } from '@/services/api';
import type { AdminResource } from '@/services/types/AdminResource';
import type { CitationResponse } from '@/features/common/types/CitationResponse';
import type { CitationStyle } from '@/features/common/types/CitationStyle';
import type { ResourcePayload } from '@/services/types/ResourcePayload';
import type { PublicLibraryResource } from '@/services/types/PublicLibraryResource';
import type { SavedLibraryPage } from '@/services/types/SavedLibraryPage';

const RESOURCES = '/api/v1/library/resources';
const PUBLIC = '/api/v1/public/library';

/**
 * The library's own endpoints — the administration list and its corrections, a reader's shelf, the
 * public catalogue's search and its citations. The screens call these, never a URL; a retraction
 * goes through workflowApi and a document through mediaApi, as in every other feature.
 *
 * In services rather than features/library/api because two features call it: the administration
 * lists and corrects resources, the public registry searches them and saves them to a shelf.
 */
export const libraryApi = {
  /** Every resource, published or not — what the administration lists. */
  resources: () => apiGet<AdminResource[]>(RESOURCES),
  /** The correction endpoint. It replaces the record outright, so the payload is always whole. */
  correct: (id: string, body: ResourcePayload) => apiPut(`${RESOURCES}/${id}`, body),
  /** One page of the reader's shelf: page, size, and q / category when the reader narrows it. */
  saved: (query: URLSearchParams) => apiGet<SavedLibraryPage>(`/api/v1/library/saved?${query}`),
  /** Every resource the reader has saved, ids only: whether a public card shows "Salvat". */
  savedIds: () => apiGet<string[]>('/api/v1/library/saved/ids'),
  /** No body: the path names the resource. PUT because saving is idempotent (LibraryController). */
  save: (resourceId: string) => apiPut(`${RESOURCES}/${resourceId}/saved`),
  unsave: (resourceId: string) => apiDelete(`${RESOURCES}/${resourceId}/saved`),
  /** A published resource's file, through the endpoint that counts the access (LIB-1). */
  downloadUrl: (id: string) => `${PUBLIC}/${id}/download`,
  /** One published resource (LIB-1); 404 for one that is not published. */
  publicResource: (id: string) => apiGet<PublicLibraryResource>(`${PUBLIC}/${id}`),
  citation: (resourceId: string, style: CitationStyle) =>
    apiGet<CitationResponse>(`${PUBLIC}/${resourceId}/citation?style=${style}`),
  /** The public catalogue's search, for the query string libraryQuery builds from the page URL. */
  searchUrl: (search: URLSearchParams) => `${PUBLIC}?${search}`,
};
